// Helps position elements in Overlook and Live Graphs equipment grids
import { useMemo } from 'react';
import { CncEquipmentExt } from '../../shared/types/cncEquipmentExt.ts';
import { trpc } from '../utils/trpc/trpc.ts';


interface Props {
    page: number;
    isSingleView: boolean;
}

/** Loads equipment coordinates and exposes positions for the selected display mode. */
export function useEquipmentGrid({
    page,
    isSingleView,
}: Props) {
    const { data: equipments } = trpc.equipments.getAllCncExt.useQuery();
    const page2Layout = useMemo(() => getPage2Layout(equipments), [equipments]);

    // Single view places compacted page 2 first, then one separator column, then all six page-1 columns.
    const separatorColumn = page2Layout.span + 1;
    const page1StartColumn = separatorColumn + 1;

    return {
        page2ColumnSpan: page2Layout.span,
        separatorColumn,
        page1StartColumn,
        /** Returns the equipment's grid position, or null when it is unpositioned or outside the selected page. */
        getPosition(equipmentId: string) {
            const equipment = equipments?.find((e) => e.id === equipmentId);
            return getGridPosition(equipment, page, isSingleView, page2Layout);
        },
    };
}

/** Finds page 2's occupied column range, retaining one column when no positioned equipment is available. */
function getPage2Layout(equipments: readonly CncEquipmentExt[] | undefined) {
    const columns = equipments
        ?.filter((e) => e.page === 2 && e.column != null && e.row != null)
        .map((e) => 6 - e.column! + 1)
        ?? [];

    // Reserve one column when page 2 is empty or coordinates have not loaded yet.
    if (columns.length === 0) return { minColumn: 1, span: 1 };

    const minColumn = Math.min(...columns);
    const maxColumn = Math.max(...columns);
    return {
        minColumn,
        span: Math.max(1, maxColumn - minColumn + 1),
    };
}

/** Converts factory coordinates to grid positions, filtering by page unless both pages are shown together. */
function getGridPosition(
    equipment: CncEquipmentExt | undefined,
    page: number,
    isSingleView: boolean,
    page2Layout: ReturnType<typeof getPage2Layout>,
) {
    if (!equipment || !equipment.column || !equipment.row) return null;
    if (isSingleView ? !equipment.page : equipment.page !== page) return null;

    // Factory coordinates run opposite to CSS grid coordinates, whose top-left cell is (1, 1).
    const column = 6 - equipment.column + 1;
    const gridRow = 6 - equipment.row + 1;

    if (!isSingleView) {
        // Named grid lines skip the extra aisle columns defined in .equipment-grid.
        return { gridColumn: `col${column}`, gridRow };
    }

    // Remove only page 2's empty outer columns, preserving gaps between equipment inside the page.
    const gridColumn = equipment.page === 2
        ? Math.max(1, column - page2Layout.minColumn + 1)
        : page2Layout.span + 1 + column;
    return {
        gridColumn,
        gridRow,
    };
}
